import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { cn } from "@/lib/utils";
import { followPlayer, unfollowPlayer } from "@/api/followedPlayers";

interface FollowPlayerButtonProps {
    playerId: number;
    isFollowed: boolean;
}

export function FollowPlayerButton({ playerId, isFollowed }: FollowPlayerButtonProps) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async () => {
            if (isFollowed) {
                await unfollowPlayer(playerId);
            } else {
                await followPlayer(playerId);
            }
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["followed-player-ids"] });
            const prevIds = queryClient.getQueryData<number[]>(["followed-player-ids"]);
            queryClient.setQueryData<number[]>(["followed-player-ids"], (old = []) =>
                isFollowed ? old.filter((id) => id !== playerId) : [...old, playerId]
            );
            return { prevIds };
        },
        onError: (_err, _vars, context) => {
            if (context?.prevIds !== undefined) {
                queryClient.setQueryData(["followed-player-ids"], context.prevIds);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["followed-player-ids"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["followed-scouting"], refetchType: "all" });
        },
    });

    return (
        <button
            type="button"
            aria-label={isFollowed ? "Unfollow player" : "Follow player"}
            disabled={mutation.isPending}
            onClick={(e) => {
                e.stopPropagation();
                mutation.mutate();
            }}
            className={cn(
                "cursor-pointer p-1 rounded transition-colors hover:bg-primary/10 disabled:opacity-50 shrink-0",
                isFollowed ? "text-primary hover:text-primary/80" : "text-on-surface-variant/40 hover:text-primary"
            )}
        >
            <Bookmark className={cn("h-4 w-4", isFollowed ? "fill-current" : "fill-none")} />
        </button>
    );
}
